// Card identity vault. Pure. No React, no Three.js, no broadcasts.
//
// ── Why this exists ──
// Face-down cards are sanitized before they cross the wire to the
// opponent: `sanitizeForFaceDown` (syncBridge.js) nulls `name`,
// `cardId`, `imageUrl`, and `foiling` so a devtools-savvy opponent
// can't read a hidden card off network traffic. That asymmetry is
// intended. The opponent's copy is *supposed* to be blank.
//
// The hazard is that the opponent's blank copy can flow back as
// authoritative. When the non-owning player grabs a face-down card and
// moves it into a synced zone (a pile via `pile:sync`, or back to a
// hand), the nameless instance is broadcast verbatim and overwrites the
// owner's good copy. `pile:sync` does `existing.cards = incoming.cards`.
// The card is then permanently identity-less on every client: it renders
// the card back, shows as a blank outline in hand, and gets filtered out
// of the cemetery "all" view because it has no name.
//
// The vault closes that hole without weakening the anti-cheat boundary:
// each client remembers the identity of every card it has *legitimately*
// known (its own cards, plus any revealed to it via a proper flip), keyed
// by the stable instance id. When an inbound sync hands back a nameless
// version of a card the client already knows, the missing fields are
// restored from the vault. A client that never legitimately saw the card
// has nothing remembered, so it stays blank. Exactly as anti-cheat
// requires.

// Identity fields that `sanitizeForFaceDown` nulls and that the vault
// is responsible for re-hydrating. Kept in lockstep with that function.
const IDENTITY_FIELDS = ['name', 'cardId', 'imageUrl', 'foiling'];

export class CardIdentityVault {
  constructor() {
    this._byId = new Map();
  }

  /**
   * Record a card's identity, keyed by its instance id. No-op unless the
   * card actually carries a known identity. A sanitized face-down copy
   * (name === null) must never poison the vault, or it would "remember"
   * a blank and defeat the whole point.
   */
  remember(card) {
    if (!card || !card.id || !card.name) return;
    this._byId.set(card.id, {
      name: card.name,
      cardId: card.cardId ?? null,
      imageUrl: card.imageUrl ?? null,
      foiling: card.foiling ?? null,
    });
  }

  /**
   * Re-hydrate a nameless card from a remembered identity. Mutates in
   * place and returns true if anything was filled in.
   *
   * Only acts on cards missing their name. The signature of a sanitized
   * face-down copy. A card that still has its name is already identified,
   * so we never clobber it (a stale reveal can't overwrite live data).
   * Within that case, only null/undefined fields are filled, so a partial
   * instance keeps whatever real fields it already has.
   */
  restore(card) {
    if (!card || !card.id || card.name) return false;
    const known = this._byId.get(card.id);
    if (!known) return false;
    let restored = false;
    for (const field of IDENTITY_FIELDS) {
      if ((card[field] === null || card[field] === undefined) && known[field] != null) {
        card[field] = known[field];
        restored = true;
      }
    }
    return restored;
  }

  has(id) {
    return this._byId.has(id);
  }

  clear() {
    this._byId.clear();
  }
}
